/**
 * attentionSettings.js — plain-language, per-device attention settings.
 *
 * These are the toggles Phase C surfaces in the parent console. They
 * exist here in Phase B so `FocusShell`, `IdleNudge`, `BreakOffer`,
 * and future features can already read them (defaults ensure no
 * regression before the UI ships).
 *
 * Storage: `mwq_attention_v1` in localStorage. Simple flat object so
 * a future backend sync can serialise it verbatim.
 */

import { useEffect, useState } from "react";

const KEY = "mwq_attention_v1";

/** Sensible defaults per the Phase B ask:
 *   • focusMode          — OFF (parent must opt in)
 *   • autoAdvance        — OFF (child stays in control by default)
 *   • breakReminders     — ON  (offered, never forced)
 *   • reducedMotion      — OFF (respects system pref already; toggle is override)
 *   • simplified         — OFF
 *   • sounds             — OFF (per PART 4: "Sounds off by default")
 *   • planSize           — "adaptive" (matches what they finish)
 *   • sessionMinutes     — 10
 */
export const DEFAULTS = {
  focusMode: false,
  autoAdvance: false,
  breakReminders: true,
  reducedMotion: false,
  simplified: false,
  sounds: false,
  planSize: "adaptive",
  sessionMinutes: 10,
  // "Make this work for me" — accessibility & readability (applied as classes on <html>)
  dyslexiaFont: false,
  textSize: "normal",      // normal | large | xl
  lineSpacing: "normal",   // normal | relaxed
  contrast: "normal",      // normal | high | lowstim
  lowDistraction: false,
  readAloudRate: 1,
  showMe: false,           // visual "show me" instructions for non-readers
  readingLevel: "standard", // simpler | standard | stretch
  glossaryLang: "",        // "" | es | fr | ar | hi | zh | pl | ur
  aiDisabled: false,       // guardian switch for Ask the Globe / AI teacher
  showCompetitive: false,  // ranks/streaks/timers are private & opt-in
};

function _read() {
  try {
    const stored = JSON.parse(localStorage.getItem(KEY) || "null");
    if (!stored || typeof stored !== "object") return { ...DEFAULTS };
    return { ...DEFAULTS, ...stored };
  } catch { return { ...DEFAULTS }; }
}

function _write(next) {
  try { localStorage.setItem(KEY, JSON.stringify(next)); } catch { /* ignore */ }
  try { window.dispatchEvent(new Event("mwq-attention-changed")); } catch { /* ignore */ }
}

export function getAttentionSettings() { return _read(); }

export function setAttentionSetting(key, value) {
  const cur = _read();
  cur[key] = value;
  _write(cur);
}

/** React hook — subscribes to changes so any component using a
 *  setting live-updates when the parent toggles it. */
export function useAttentionSettings() {
  const [s, setS] = useState(_read);
  useEffect(() => {
    const refresh = () => setS(_read());
    window.addEventListener("mwq-attention-changed", refresh);
    window.addEventListener("storage", refresh);
    return () => {
      window.removeEventListener("mwq-attention-changed", refresh);
      window.removeEventListener("storage", refresh);
    };
  }, []);
  return s;
}
